import dayjs from "~/config/dayjs";
/**
 * Dicionários de idiomas para formatação de data
 */
const languageDictionaries = {
  pt: {
    days: ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'],
    months: ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'],
    timePrefix: 'às',
    daySeparator: 'de',
    yearSeparator: 'de'
  },
  en: {
    days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    timePrefix: 'at',
    daySeparator: 'of',
    yearSeparator: ','
  },
  es: {
    days: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    months: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
    timePrefix: 'a las',
    daySeparator: 'de',
    yearSeparator: 'de'
  }
};

/**
 * Mapeia os tipos de idioma do business para os códigos internos
 */
const languageMapping = {
  'portuguese': 'pt',
  'english': 'en', 
  'spanish': 'es',
  'portugal': 'pt' // Portugal usa o mesmo formato do português brasileiro
};

/**
 * Formata uma data para o padrão por extenso em diferentes idiomas
 * @param {Date|string} date - Data a ser formatada
 * @param {string} language - Idioma ('pt', 'en', 'es', 'portuguese', 'english', 'spanish', 'portugal') - padrão: 'pt'
 * @param {boolean} includeTime - Se deve incluir o horário (padrão: true)
 * @param {string} timeZone - Timezone para conversão (opcional, ex: 'America/Rio_Branco')
 * @returns {string} Data formatada no padrão do idioma selecionado
 */
export const formatDateToLanguage = (date, language = 'pt', includeTime = true, timeZone = null) => {
  if (!date) return '';
  
  let dateObj = dayjs(date);
  if (!dateObj.isValid()) return '';
  
  // Se um timezone foi especificado, converte para ele
  if (timeZone) {
    dateObj = dateObj.utc().tz(timeZone);
  }
  
  // Mapeia o idioma para o código interno
  const mappedLanguage = languageMapping[language] || language;
  
  // Fallback para português se o idioma não for suportado
  const lang = languageDictionaries[mappedLanguage] || languageDictionaries.pt;
  
  const diaSemana = lang.days[dateObj.day()];
  const dia = dateObj.date();
  const mes = lang.months[dateObj.month()];
  const ano = dateObj.year();
  
  let dataFormatada;
  
  // Formatação específica por idioma
  switch (mappedLanguage) {
    case 'en':
      // Inglês: "Tuesday, May 14 at 14:00"
      dataFormatada = `${diaSemana}, ${mes} ${dia}`;
      // Adiciona o ano se não for o ano atual
      const anoAtual = dayjs().year();
      if (ano !== anoAtual) {
        dataFormatada += lang.yearSeparator + ' ' + ano;
      }
      break;
    case 'es':
      // Espanhol: "martes, 14 de mayo a las 14:00"
      dataFormatada = `${diaSemana}, ${dia} ${lang.daySeparator} ${mes}`;
      // Adiciona o ano se não for o ano atual
      if (ano !== dayjs().year()) {
        dataFormatada += ` ${lang.yearSeparator} ${ano}`;
      }
      break;
    default: // pt
      // Português: "terça-feira, 14 de maio às 14:00"
      dataFormatada = `${diaSemana}, ${dia} ${lang.daySeparator} ${mes}`;
      // Adiciona o ano se não for o ano atual
      if (ano !== dayjs().year()) {
        dataFormatada += ` ${lang.yearSeparator} ${ano}`;
      }
      break;
  }
  
  // Adiciona o horário se solicitado
  if (includeTime) {
    const horaFormatada = dateObj.format('HH:mm');
    dataFormatada += ` ${lang.timePrefix} ${horaFormatada}`;
  }
  
  return dataFormatada;
};

/**
 * Formata uma data para o padrão brasileiro por extenso (mantido para compatibilidade)
 * @param {Date|string} date - Data a ser formatada
 * @param {boolean} includeTime - Se deve incluir o horário (padrão: true)
 * @returns {string} Data formatada no padrão "terça-feira, 14 de maio às 14:00"
 */
export const formatDateToPortuguese = (date, includeTime = true) => {
  return formatDateToLanguage(date, 'pt', includeTime);
};

/**
 * Formata uma data para exibição resumida
 * @param {Date|string} date - Data a ser formatada
 * @returns {string} Data formatada no padrão "14/05/2025"
 */
export const formatDateShort = (date) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  const dia = dateObj.getDate().toString().padStart(2, '0');
  const mes = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const ano = dateObj.getFullYear();
  
  return `${dia}/${mes}/${ano}`;
};

/**
 * Formata apenas o horário
 * @param {Date|string} date - Data a ser formatada
 * @returns {string} Horário formatado no padrão "14:00"
 */
export const formatTime = (date, timeZone = null) => {
  if (!date) return '';
  
  let dateObj = dayjs(date);
  
  if (!dateObj.isValid()) return '';
  
  // Se um timezone foi especificado, converte para ele
  if (timeZone) {
    dateObj = dateObj.utc().tz(timeZone);
  }
  
  return dateObj.format('HH:mm');
};

/**
 * Formata data e hora para exibição completa em português (mantido para compatibilidade)
 * @param {Date|string} date - Data a ser formatada
 * @param {string} language - Idioma ('pt', 'en', 'es') - padrão: 'pt'
 * @returns {string} Data e hora formatadas no padrão do idioma selecionado
 */
export const formatDateTimeFull = (date, language = 'pt') => {
  return formatDateToLanguage(date, language, true);
};

/**
 * Formata apenas a data sem o horário
 * @param {Date|string} date - Data a ser formatada
 * @param {string} language - Idioma ('pt', 'en', 'es') - padrão: 'pt'
 * @returns {string} Data formatada no padrão do idioma selecionado
 */
export const formatDateOnly = (date, language = 'pt') => {
  return formatDateToLanguage(date, language, false);
};

/**
 * Formata data para inglês
 * @param {Date|string} date - Data a ser formatada
 * @param {boolean} includeTime - Se deve incluir o horário (padrão: true)
 * @returns {string} Data formatada em inglês
 */
export const formatDateToEnglish = (date, includeTime = true) => {
  return formatDateToLanguage(date, 'en', includeTime);
};

/**
 * Formata data para espanhol
 * @param {Date|string} date - Data a ser formatada
 * @param {boolean} includeTime - Se deve incluir o horário (padrão: true)
 * @returns {string} Data formatada em espanhol
 */
export const formatDateToSpanish = (date, includeTime = true) => {
  return formatDateToLanguage(date, 'es', includeTime);
};

/**
 * Função utilitária para formatar data baseada no idioma do business
 * @param {Date|string} date - Data a ser formatada
 * @param {string} businessLanguage - Idioma do business ('portuguese', 'english', 'spanish', 'portugal')
 * @param {boolean} includeTime - Se deve incluir o horário (padrão: true)
 * @param {string} timeZone - Timezone para conversão (opcional, ex: 'America/Rio_Branco')
 * @returns {string} Data formatada no padrão do idioma do business
 */
export const formatDateByBusinessLanguage = (date, businessLanguage, includeTime = true, timeZone = null) => {
  return formatDateToLanguage(date, businessLanguage, includeTime, timeZone);
};

export default {
  formatDateToLanguage,
  formatDateToPortuguese,
  formatDateToEnglish,
  formatDateToSpanish,
  formatDateByBusinessLanguage,
  formatDateShort,
  formatTime,
  formatDateTimeFull,
  formatDateOnly
};
